import React, { useState, useEffect } from 'react';
import { View, Text, Image } from 'react-native';
import moment from 'moment';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import DashedLine from 'react-native-dashed-line';

import { notchTop, width, scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';

import CodeInput from '@components/codeInput';
import SetupPopup from './components/SetupPopup';
import SuccessScreen from './components/SuccessScreen';
import RunningConnector from './components/RunningConnector';
import { validateOnboarding, onboardConnector, saveData, logOnboarding } from '@reducers/onBoarding';
import { anonymSignalR } from '@reducers/signalR';
import { Alert, getSecureValue, convertWebBoolean, downloadConnector, navigateTo } from '@helpers';
import Button from '@components/button';

import ScanQRCode from '@assets/scanQRCode.png';
import GooglePlay from '@assets/download/googlePlay.svg';
import AppleStore from '@assets/download/appleStore.svg';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';

const ConnectorSetup = ({ C, onBoarding, route, validateOnboarding, onboardConnector, anonymSignalR, saveData, logOnboarding }) => {
  const [loading, setLoading] = useState(true);
  const [isSuccess, setSuccess] = useState(false);
  const [code, setCode] = useState('');

  const title = route?.params?.title;

  const isFromModal = convertWebBoolean(route?.params?.fromModal);

  const globalCode = onBoarding?.unregisteredConnector?.sessionKey;

  const isRunningConnector = onBoarding?.registeredConnector?.isRunning;

  const min15 = 15 * 60;
  const pass = moment.utc().diff(moment.utc(onBoarding?.unregisteredConnector?.startedOn), 's');
  const left = min15 - pass;
  const canDoFastSetup = left > 0 && onBoarding?.onboardingStatus === 'UnregisteredConnectorFound';
  const showDownloadBlock = onBoarding?.onboardingStatus === 'NoConnectorFound';
  const isMultipleConnectors = onBoarding?.onboardingStatus === 'MultipleConnectorsFound';

  useEffect(() => {
    if (globalCode && isFromModal && !isRunningConnector) {
      submit(globalCode);
    } else {
      if (isFromModal) {
        navigateTo('ConnectorSetup', { fromModal: false });
      }
    }
  }, [isFromModal]);

  useEffect(() => {
    if (globalCode && isFromModal && !isRunningConnector) {
      return;
    }
    (async () => {
      const isAuthorized = getSecureValue('token');
      if (isAuthorized) {
        await validateOnboarding(false, false);
        setLoading(false);
      } else {
        anonymSignalR((d) => {
          validateOnboarding(false, true);
          setLoading(false);
        });
      }
    })();
  }, []);

  const onInput = async (text) => {
    setCode(text);
    if (text.length === 8) {
      logOnboarding(`Component: Connect. Action: User entered code. Code: ${text}`, { component: 'Connect', action: 'User entered code' });
      await submit(text);
    }
  };

  const submit = async (c) => {
    setLoading(true);
    const code = c || globalCode;
    const isAuthorized = getSecureValue('token');
    if (isAuthorized) {
      saveData({ code });
      logOnboarding(`Component: Connect. Action: Start onboarding`, { component: 'Connect', action: 'Start onboarding' });
      const result = await onboardConnector(null, false);
      if (result?.connectorId) {
        setSuccess(true);
      } else {
        Alert({ title: result?.message || 'Wrong code' });
      }
    } else {
      logOnboarding(`Component: Connect. Action: Validate onboarding`, { component: 'Connect', action: 'Validate onboarding' });
      const result = await validateOnboarding(code, true);
      if (result?.onboardingStatus === 'UnregisteredConnectorConnectionEstablished') {
        setSuccess(true);
      } else {
        Alert({ title: 'Wrong code' });
      }
    }
    setLoading(false);
  };

  if (isSuccess) {
    return <SuccessScreen {...{ C, title, logOnboarding }} />;
  }

  if (loading || !onBoarding.isLoaded) {
    return (
      <View style={{ width: '100%', flex: 1, paddingTop: notchTop, backgroundColor: C('Background/Light/Detail') }}>
        <View style={{ width: '100%', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={[font['Body'], { color: C('Label/Light/Primary'), textAlign: 'center', width: '100%' }]}>
            {i18n.t('Connecting to your Connector...')}
          </Text>
        </View>
      </View>
    );
  }

  if (isRunningConnector) {
    return <RunningConnector {...{ C, onBoarding, logOnboarding }} />;
  }

  if (canDoFastSetup) {
    return (
      <View
        style={{
          width: '100%',
          flex: 1,
          alignItems: 'center',
          backgroundColor: C('Background/Light/System'),
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
        }}
      >
        <View
          style={[
            getBoxShadowStyle({ type: 'Wide', C }),
            {
              width: scale(400),
              backgroundColor: C(modalFieldBg),
              borderRadius: scale(40),
              alignItems: 'center',
            },
          ]}
        >
          <SetupPopup {...{ C, logOnboarding }} />
        </View>
      </View>
    );
  }

  return (
    <View style={{ width: '100%', flex: 1, backgroundColor: C('Background/Light/Detail'), paddingTop: notchTop }}>
      <View style={{ width: '100%', flex: 1, alignItems: 'center' }}>
        <View style={{ maxWidth: width, paddingHorizontal: 20, width: '100%', alignItems: 'center', marginTop: 90 }}>
          <View style={{ flexDirection: 'row' }}>
            {showDownloadBlock && (
              <View>
                <View style={{ paddingRight: 39, marginTop: -20 }}>
                  <View
                    style={{
                      backgroundColor: C('Other/Light/Logo/Smart'),
                      borderRadius: 36,
                      flexDirection: 'row',
                      paddingLeft: 22,
                      paddingRight: 6,
                      paddingVertical: 6,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary'), marginRight: 13 }]}>{i18n.t('Step')}</Text>
                    <View
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 27,
                        borderColor: C('Label/Light/AlwaysLight Primary'),
                        borderWidth: 2,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('1')}</Text>
                    </View>
                  </View>
                  <View style={{ width: '100%', paddingRight: 30, alignItems: 'flex-end', marginTop: -5 }}>
                    <DashedLine
                      axis="vertical"
                      style={{ height: 265 }}
                      dashThickness={5}
                      dashLength={10}
                      dashGap={10}
                      dashColor={C('Other/Light/Logo/Smart')}
                    />
                  </View>
                  <View
                    style={{
                      backgroundColor: C('Other/Light/Logo/Smart'),
                      borderRadius: 36,
                      flexDirection: 'row',
                      paddingLeft: 22,
                      paddingRight: 6,
                      paddingVertical: 6,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary'), marginRight: 13 }]}>{i18n.t('Step')}</Text>
                    <View
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 27,
                        borderColor: C('Label/Light/AlwaysLight Primary'),
                        borderWidth: 2,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('2')}</Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
            <View style={{ width: 730 }}>
              {showDownloadBlock && (
                <View>
                  <Text style={[font['Title 1'], { color: C('Label/Light/Primary') }]}>
                    {i18n.t('Download and Install the Connector to see your CNC Machines.')}
                  </Text>
                  <Text style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginTop: 28 }]}>
                    {i18n.t('The Connector is a free application for Microsoft Windows that connects to CNC machines in your company.')}
                  </Text>
                  <View style={{ marginTop: 28, marginBottom: 100 }}>
                    <Button
                      {...{
                        name: i18n.t(`Download Connector`),
                        onPress: downloadConnector,
                        height: 48,
                        width: 460,
                        borderRadius: 40,
                      }}
                    />
                  </View>
                </View>
              )}
              <Text style={[font['Title 1'], { color: C('Label/Light/Primary') }]}>
                {i18n.t(showDownloadBlock ? 'Link the Connector to your company' : 'Link your Connector to your Company')}
              </Text>
              {onBoarding?.unregisteredConnector && (
                <Text style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginTop: 28 }]}>
                  {i18n.t(
                    isMultipleConnectors
                      ? 'You have multiple Smart Factory Connectors running on your local network.'
                      : showDownloadBlock
                      ? 'If you have installed the Connector already, enter the link code that is shown on the Connector.'
                      : 'You have a Smart Factory Connector running on your local network on: {{localHost}}',
                    {
                      localHost: onBoarding?.unregisteredConnector?.localHost || onBoarding?.registeredConnector?.localHost
                    }
                  )}
                </Text>
              )}
              {!showDownloadBlock && (
                <Text style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginTop: 28 }]}>
                  {i18n.t(
                    isMultipleConnectors
                      ? 'Enter the Code that is shown the Connector you would like to use:'
                      : 'Enter the Code that is shown on this Connector:'
                  )}
                </Text>
              )}
            </View>
          </View>
          <View style={{ marginTop: 50, width: '100%', alignItems: 'center' }}>
            <View style={{ marginTop: 12, width: '100%' }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginTop: 12,
                }}
              >
                <CodeInput {...{ C, value: code, setValue: onInput }} />
              </View>
            </View>
          </View>
          <View style={{ flexDirection: 'row', height: 30, justifyContent: 'center', alignItems: 'center', marginTop: 28 }}>
            <View style={{ width: 340, height: 1, backgroundColor: C('Hairline/Light/No trans') }} />
            <Text style={[font['Subhead 1'], { color: C('Label/Light/Secondary'), paddingHorizontal: 11 }]}>{i18n.t('OR')}</Text>
            <View style={{ width: 340, height: 1, backgroundColor: C('Hairline/Light/No trans') }} />
          </View>
          <View style={{ width: '100%', alignItems: 'center' }}>
            <View style={{ width: 730 }}>
              <Text style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginTop: 28 }]}>
                {i18n.t(
                  showDownloadBlock
                    ? 'Or scan the QR code with the Smart Factory app'
                    : 'Scan the QR code shown on the Connector with the Smart Factory app'
                )}
              </Text>
            </View>
          </View>
          <View style={{ width: '100%' }}>
            <Image source={ScanQRCode} style={{ width: '100%', height: '100%', height: 520, marginLeft: 225 }} resizeMode={'contain'} />
          </View>

          <View style={{ flexDirection: 'row' }}>
            <View style={{ paddingRight: 39, marginTop: -20, opacity: 0 }}>
              <View
                style={{
                  backgroundColor: C('Other/Light/Logo/Smart'),
                  borderRadius: 36,
                  flexDirection: 'row',
                  paddingLeft: 22,
                  paddingRight: 6,
                  paddingVertical: 6,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary'), marginRight: 13 }]}>{i18n.t('Step')}</Text>
                <View
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 27,
                    borderColor: C('Label/Light/AlwaysLight Primary'),
                    borderWidth: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Text style={[font['Title 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('1')}</Text>
                </View>
              </View>
            </View>
            <View style={{ width: '100%', width: 730, paddingBottom: 50 }}>
              <View>
                <Text style={[font['Large Title'], { color: C('Label/Light/Primary') }]}>{i18n.t('Our apps are free')}</Text>
              </View>
              <View style={{ flexDirection: 'row', marginTop: 25 }}>
                <View style={{ marginRight: 50 }}>
                  <AppleStore width={270} height={80} />
                </View>
                <GooglePlay width={270} height={80} />
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default connect(({ onBoarding }) => ({ onBoarding }), { validateOnboarding, onboardConnector, anonymSignalR, saveData, logOnboarding })(
  ColorWrapper(ConnectorSetup)
);
